import React, { FC, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import "./I18n/i18n.config";
import { useAppSelector, useAppDispatch } from "./store/index";
import ROUTES from "./router/routes";
import { setIsAuthorized, getUserInfo, logout } from "./store/user/slice";
import {
  getModules,
  getGeoList,
  getCurrencyList,
  getPayoutCurrencyList,
  getLocalesList,
  getServicesList,
  getCountryList,
} from "./store/settings/slice";
import { ModalTypes } from "./configs/types";
import { openModal, closeModal } from "./store/modals/slice";

import { currentBrand } from "./configs/brands";

import "react-datepicker/dist/react-datepicker.css";
import "./styles/Main.scss";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const Root: FC = () => {
  return <Outlet />;
};

const App: React.FC = () => {
  const isLogged = useAppSelector((store) => store.auth.isAuthorized);
  const isModulesFetched = useAppSelector(
    (store) => store.settings.isModulesFetched,
  );

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const permissions = usePermissionHelper();

  useEffect(() => {
   
    document.title = currentBrand.title;

    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute("content", currentBrand.description);
    }

    const favicon =
      document.querySelector("link[rel='icon']") ||
      document.querySelector("link[rel='shortcut icon']");

    if (favicon) {
      favicon.setAttribute("href", currentBrand.faviconUrl);
    }
  }, []);

  useEffect(() => {
    if (!isModulesFetched && isLogged) {
      dispatch(getModules());
      dispatch(getGeoList());
      dispatch(getCurrencyList());
      dispatch(getPayoutCurrencyList());
      dispatch(getLocalesList());
      dispatch(getServicesList());
      dispatch(getCountryList());
    }
  }, [isModulesFetched, isLogged]);

  useEffect(() => {
    if (searchParams.get("logOut") !== null) {
      dispatch(logout());
    }
    if (searchParams.get("activeModal") !== null) {
      dispatch(
        openModal({
          activeModal: searchParams.get("activeModal") as ModalTypes,
        }),
      );
    }
  }, [searchParams, window.location]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (isLogged === false && !token) {
      navigate(`${ROUTES.LOGIN}`);
    }
    if (isLogged === false && token) {
      dispatch(setIsAuthorized(true));
      dispatch(getUserInfo());
    }
  }, [isLogged]);

  useEffect(() => {
    if (pathname === ROUTES.LOGIN) {
      dispatch(closeModal());
      return;
    };

    if (pathname === ROUTES.DASHBOARD) return;

    if (permissions && !permissions.read) {
      navigate(ROUTES.DASHBOARD);
    }

  }, [permissions, pathname]);

  const token = localStorage.getItem("token");

  if (!isLogged && !token && window.location.pathname !== ROUTES.LOGIN) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <Root />;
};

export default App;
